import {
  Form,
  Select,
  Dropdown,
  DatePicker,
  Input,
  Button,
  InputNumber,
  Card,
  Flex,
  Switch,
  Descriptions,
  Row,
  Col,
  Tooltip,
  Popover,
  Divider,
  Modal,
  message,
  Space,
} from 'antd';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import APIList from '../../../../http/ApiList';
import { AppContext } from '../../../../contexts/AppContext';
import { GLOBAL_EVENT } from '../../../../consts/common/global-event';
import { isEqual, isNil, sortBy } from 'lodash';
import { COMPARE, DIMENSION, EVALUATION_PERIOD, FIELD_CATEGORY, FIELD_IMPORTANT, PAGE_ID } from '../../../../enum/common.enum';
import { FilterOutlined, PlusCircleOutlined, SettingOutlined, SwapOutlined } from '@ant-design/icons';
import { getS3Object } from '@meteor/frontend-core';
import dayjs from 'dayjs';
import { LabelTag } from '../../../common/label-tag';
import { DraggableTags } from '../../../common/draggable-tags';
import { getDefaultValue } from '../../../../utils/farecast-util';
import { NAME_PATH, useTralitSetting } from '../../../../hooks/useTralitSetting';
import { useLabel } from '../../../../hooks/useLabel';

export type FilterItem = {
  key: string;
  title: string;
  type: 'title' | 'input' | 'inputNumber' | 'btn' | 'submit' | 'calendar' | 'groupBtn' | 'multi' | 'single';
  values: string[];
  defaultValue: string | string[];
  actions: any;
  width: number;
};

const ForecastHeader: React.FC<any> = (props) => {
  const [form] = Form.useForm();
  const { event$, color, getCalenderMap, tralitSettings } = useContext(AppContext);
  const { fontSize } = useTralitSetting({ namePath: NAME_PATH.PREFERENCE });
  const label = useLabel();
  const { property } = props;

  // Drill Down Fields
  const [drillFields, setDrillFields] = useState([]);
  // Loading
  const [loading, setLoading] = useState(true);
  // URL Params
  const searchParams = new URLSearchParams(location.search);
  // Condition/URL Param对象
  const searchValues: any = useMemo(() => {
    const params = (property.condition ? property.condition : Object.fromEntries(searchParams)) || {};
    if (Object.keys(params).length > 0) {
      return Object.keys(params).reduce((acc, cur) => {
        if (cur === 'drillFields') {
          Object.keys(params[cur]).forEach((key) => {
            acc[key] = params[cur][key].split(',');
          });
        } else {
          acc[cur] = params[cur];
        }
        return acc;
      }, {});
    }
    return {};
  }, [searchParams, property.condition]);

  // 设置页面打开Flag
  const [isSettingOpen, setSettingOpen] = useState(false);
  // info展示数据
  const [infoItems, setInfoItems] = useState([]);
  // 下拉框数据
  const [options, setOptions] = useState(() => ({
    fiscalYear: ['FY23', 'FY24', 'FY25', 'FY26', 'FY27'].map((value) => ({ key: value, label: value, value })),
    evaluationPeriod: ['今月単月', '先週単週', '全期', '上期', '下期', '今月まで', '実績のみ', '見込のみ'].map((value) => ({
      key: value,
      label: value,
      value,
    })),
    compare: [
      {
        key: 'snapshot',
        value: 'snapshot',
        label: '断面',
      },
      {
        key: 'period',
        value: 'period',
        label: '期間',
      },
      {
        key: 'plan',
        value: 'plan',
        label: label.plan,
      },
      {
        key: 'prev_year',
        value: 'prev_year',
        label: '前年',
      },
      {
        key: 'prev_month',
        value: 'prev_month',
        label: '前月',
      },
    ],
    dimension: [
      { key: DIMENSION.DAY, value: DIMENSION.DAY, label: '日' },
      { key: DIMENSION.WEEK, value: DIMENSION.WEEK, label: '週' },
      { key: DIMENSION.MONTH, value: DIMENSION.MONTH, label: '月' },
    ],
    snapshots: [],
  }));
  // 比较对象监听
  const compare = Form.useWatch('compare', form);

  // 保存的Columns设置
  const [drillSaveFields, setDrillSaveFields] = useState<any[]>();

  useEffect(() => {
    form.setFieldValue('drillSaveFields', drillSaveFields);
  }, [drillSaveFields]);

  /**
   * 获取初始化Filter数据
   * API获取最新断面
   */
  const getFilterData = () => {
    setLoading(true);
    APIList.getHeader()
      .post({ websocketRoute: 'WebsocketOpenaiEvent', client_account: tralitSettings.account })
      .then(async (res) => {
        const { snapshots, fields } = await getS3Object(process.env.REACT_APP_BUCKET_NAME, res.bucketKey);
        const [currentSnapshot, targetSnapshot] = snapshots;
        const drillFields = fields.filter(
          (field) =>
            field.category === FIELD_CATEGORY.DRILL_DOWN &&
            field.important >= FIELD_IMPORTANT.LEVEL0 &&
            field.classification?.includes('category')
        );
        setOptions((pref) => ({ ...pref, snapshots: snapshots.map((snapshot) => ({ value: snapshot, label: snapshot })) }));
        // 存储Drill Down Fields
        setDrillFields(drillFields);
        const fiscalYear = searchValues.fiscalYear || getDefaultValue(tralitSettings.account, 'fiscalYear');
        const evaluationPeriod = searchValues.evaluationPeriod || EVALUATION_PERIOD.FULL_PERIOD;
        const compare = searchValues.compare || COMPARE.PLANNED_RATIO;
        const switchValue = searchValues.switchValue || true;
        const dimension = searchValues.dimension || DIMENSION.MONTH;
        const selectedKpis = searchValues.selectedKpis;
        const initDrillFields = {
          ...drillFields.reduce((acc, cur) => {
            if (Object.keys(searchValues).length) {
              acc[cur.fieldKey] =
                searchValues && searchValues[cur.fieldKey] ? searchValues[cur.fieldKey] : cur.options?.map((option) => option);
            } else {
              acc[cur.fieldKey] = cur.options.map((value) => value);
            }
            return acc;
          }, {}),
        };
        // 被选择过的Drill down
        const selectedDrillData = drillFields.reduce((acc, drillField) => {
          if (!drillField.options.every((optionValue) => initDrillFields[drillField.fieldKey].includes(optionValue))) {
            acc.push({
              fieldKey: drillField.fieldKey,
              values: initDrillFields[drillField.fieldKey],
            });
          }
          return acc;
        }, []);
        // 计算集計期間
        const aggregationPeriod = getAggregationPeriod(currentSnapshot, fiscalYear, evaluationPeriod);
        const drillSaveFields = searchValues.drillSaveFields
          ? searchValues.drillSaveFields.map((fieldKey) => drillFields.find((drillField) => drillField.fieldKey === fieldKey))
          : drillFields.filter((drillField) => drillField.important > FIELD_IMPORTANT.LEVEL0);
        const initData: any = {
          currentSnapshot,
          targetSnapshot,
          fiscalYear,
          evaluationPeriod,
          aggregationPeriod,
          compare,
          switchValue,
          dimension,
          selectedKpis,
          selectedDrillData,
          drillSaveFields,
        };
        initData.drillFields = initDrillFields;
        form.setFieldsValue(initData);
        // 设置Desc数据
        setDesc(initData, drillFields);
        // 设置保存的Drill Down Fields
        setDrillSaveFields(drillSaveFields);

        event$.emit({
          eventType: GLOBAL_EVENT.PAGE_INIT,
          data: initData,
          pageId: PAGE_ID.FORECAST,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /**
   * 设置Desc
   * @param data
   */
  const setDesc = (data, drillFields) => {
    // 根据value获取下拉框的label
    const getOptionLabel = (key, value) => {
      return options[key]?.find((option) => option.value === value)?.label;
    };

    setInfoItems([
      {
        key: 'currentSnapshot',
        label: '断面',
        children: data.currentSnapshot,
      },
      {
        key: 'fiscalYear',
        label: '財務年度',
        children: getOptionLabel('fiscalYear', data.fiscalYear),
      },
      ...drillFields.reduce((acc, drillField) => {
        const findField = drillFields.find((field) => drillField.fieldKey === field.fieldKey);
        if (!isEqual(sortBy(findField.options), sortBy(data.drillFields[drillField.fieldKey]))) {
          acc.push({
            key: drillField.fieldKey,
            label: drillField.label,
            children: data.drillFields ? data.drillFields[drillField.fieldKey]?.join(', ') : '',
          });
        }
        return acc;
      }, []),
    ]);
  };

  /**
   * 初始化
   */
  useEffect(() => {
    getFilterData();
  }, [tralitSettings.account]);

  /**
   * 通过日历获取集計期間
   */
  const getAggregationPeriod = (snapshot, fiscalYear, evaluationPeriod) => {
    const calenderMap = getCalenderMap(snapshot);
    if (calenderMap && calenderMap[fiscalYear]) {
      return calenderMap[fiscalYear][evaluationPeriod].period;
    }
    return null;
  };

  /**
   * 評価期間修改联动集計期間
   */
  const evaluationPeriodChange = (values) => {
    if (values.evaluationPeriod || values.fiscalYear) {
      const aggregationPeriod = getAggregationPeriod(
        form.getFieldValue('currentSnapshot'),
        form.getFieldValue('fiscalYear'),
        form.getFieldValue('evaluationPeriod')
      );
      form.setFieldValue('aggregationPeriod', aggregationPeriod);
    }
  };

  /**
   * 根据比较对象得到联动表单
   */
  const CompareComponent = useCallback(() => {
    const isSameTheIntervalOfTwoDatePeriods = (firstData: any, secondData: any) => {
      if (secondData.length === 0) {
        return true;
      }

      const firstStartDate = dayjs(firstData[0]);
      const firstEndDate = dayjs(firstData[1]);
      const secondStartDate = dayjs(secondData[0]);
      const secondEndDate = dayjs(secondData[1]);
      const firstDiff = firstEndDate.diff(firstStartDate, 'month');
      const secondDiff = secondEndDate.diff(secondStartDate, 'month');

      return firstDiff === secondDiff;
    };

    const map = {
      snapshot: (
        <>
          <Form.Item name="targetSnapshot" noStyle>
            <Select style={{ width: 120 }} options={options.snapshots} />
          </Form.Item>
        </>
      ),
      period: (
        <>
          <Space split="~">
            <Form.Item name="currentPeriod" label="前期間" rules={[{ required: true }]} noStyle>
              <DatePicker.RangePicker
                picker="month"
                style={{ width: 180 }}
                onChange={() => {
                  const curDate = form.getFieldValue('currentPeriod') || [];
                  const tarDate = form.getFieldValue('targetPeriod') || [];
                  if (!isSameTheIntervalOfTwoDatePeriods(curDate, tarDate)) {
                    message.error('2つの期間の間隔は一致する必要があります', 5);
                    form.setFieldValue('currentPeriod', []);
                  }
                }}
              />
            </Form.Item>
            <Form.Item name="targetPeriod" label="後期間" rules={[{ required: true }]} noStyle>
              <DatePicker.RangePicker
                picker="month"
                style={{ width: 180 }}
                onChange={() => {
                  const curDate = form.getFieldValue('currentPeriod') || [];
                  const tarDate = form.getFieldValue('targetPeriod') || [];
                  if (!isSameTheIntervalOfTwoDatePeriods(curDate, tarDate)) {
                    message.error('2つの期間の間隔は一致する必要があります', 5);
                    form.setFieldValue('targetPeriod', []);
                  }
                }}
              />
            </Form.Item>
          </Space>
        </>
      ),
      plan: <></>,
      prev_year: <></>,
      prev_month: <></>,
    };
    return (
      <>
        <Form.Item label={'比較対象'}>
          <Space>
            <Form.Item name="compare" noStyle>
              <Select style={{ width: 120 }} options={options.compare} />
            </Form.Item>
            {map[compare]}
            <Form.Item name="switchValue" noStyle>
              <Switch unCheckedChildren="差分" checkedChildren="比" style={{ background: color.primaryColor }} />
            </Form.Item>
          </Space>
        </Form.Item>
      </>
    );
  }, [form, options, compare]);

  const DrillFieldsComponent = useCallback(() => {
    return (
      <>
        {drillFields.map(({ fieldKey, label, options, parentKey }) => {
          return (
            <Form.Item key={fieldKey} name={['drillFields', fieldKey]} label={label} labelCol={{ span: 6 }} layout="horizontal">
              <Select
                allowClear
                mode="multiple"
                maxTagCount="responsive"
                style={{ width: 220 }}
                showSearch
                filterOption={(input, option) => ((option?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())}
                options={sortBy(options).map((item) => ({
                  key: item,
                  label: item,
                  value: item,
                }))}
              />
            </Form.Item>
          );
        })}
      </>
    );
  }, [drillFields]);

  const DrillDownsComponent = useCallback(() => {
    const balanceDrillFields = drillFields.reduce((acc, drillField) => {
      if (drillSaveFields.findIndex((drillSaveField) => drillSaveField.fieldKey === drillField.fieldKey) === -1) {
        acc.push({
          ...drillField,
          key: drillField.fieldKey,
          onClick: ({ key }) => {
            setDrillSaveFields((pref) => [...pref, drillFields.find((drillField) => drillField.fieldKey === key)]);
          },
        });
      }
      return acc;
    }, []);
    return (
      <>
        <Form.Item
          name="drillSaveFields"
          label="ドリルダウン軸"
          rules={[
            {
              required: true,
              message: 'ドリューを選択してください！',
            },
            {
              validator: async (_, drillSaveFields) => {
                const hasErr = drillSaveFields.some((drillSaveField) => {
                  const parIndex = drillSaveFields.findIndex((field) => field.fieldKey === drillSaveField.parentKey);
                  const nurIndex = drillSaveFields.findIndex((field) => field.fieldKey === drillSaveField.fieldKey);
                  if (parIndex > nurIndex) {
                    return true;
                  }
                  return false;
                });
                if (hasErr) {
                  return Promise.reject(new Error('親子関係に基づいて並べ替えてください。'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <DraggableTags
            items={drillSaveFields.map((drillSaveField) => ({
              id: drillSaveField.fieldKey,
              text: drillSaveField,
            }))}
            labelProp="label"
            closable
            onClose={(items) => {
              setDrillSaveFields(items.map((item) => item.text));
            }}
            onDragEnd={(items) => {
              setDrillSaveFields(items.map((item) => item.text));
            }}
          />
          {balanceDrillFields && balanceDrillFields.length > 0 && (
            <Dropdown menu={{ items: balanceDrillFields }} trigger={['click']}>
              <a
                onClick={(e) => {
                  e.preventDefault();
                }}
              >
                <Space>
                  <PlusCircleOutlined />
                </Space>
              </a>
            </Dropdown>
          )}
        </Form.Item>
      </>
    );
  }, [drillFields, drillSaveFields]);

  const handleOk = () => {
    // validate
    form.validateFields().then(() => {
      const selectedKpis = searchValues.selectedKpis;
      // 被选择过的Drill down
      const selectedDrillData = drillFields.reduce((acc, drillField) => {
        if (!drillField.options.every((optionValue) => form.getFieldValue(['drillFields', drillField.fieldKey]).includes(optionValue))) {
          acc.push({
            fieldKey: drillField.fieldKey,
            values: form.getFieldValue(['drillFields', drillField.fieldKey]),
          });
        }
        return acc;
      }, []);
      const data = {
        ...form.getFieldsValue(),
        drillSaveFields,
        selectedKpis,
        selectedDrillData,
      };
      const eventInfo = {
        eventType: property.triggerEventType,
        data,
        pageId: PAGE_ID.FORECAST,
      };
      event$.emit(eventInfo);
      setSettingOpen(false);
      setDesc(data, drillFields);
    });
  };

  return (
    <>
      <Card style={{ height: '100%' }} styles={{ body: { padding: 0, height: '100%' } }}>
        <Flex style={{ height: '100%', alignItems: 'center' }}>
          <Flex style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
            <span style={{ margin: '0px 14px', fontSize: fontSize.title, fontWeight: 'bold' }}>業績サマリー </span>
            <Flex gap="4px 0" wrap style={{ flex: 1 }}>
              {infoItems.map((item) => {
                return <LabelTag key={item.key} label={item.label} value={item.children} />;
              })}
            </Flex>
          </Flex>
          <Flex style={{ width: 60, justifyContent: 'center' }}>
            {!loading && (
              <SettingOutlined
                style={{ cursor: 'pointer', fontSize: 18 }}
                onClick={() => {
                  setSettingOpen(true);
                }}
              />
            )}
          </Flex>
        </Flex>
      </Card>
      <Modal
        width={850}
        title="業績サマリー"
        open={isSettingOpen}
        onOk={handleOk}
        onCancel={() => {
          setSettingOpen(false);
        }}
      >
        <Form
          form={form}
          labelCol={{ span: 6 }}
          onValuesChange={(values) => {
            evaluationPeriodChange(values);
          }}
        >
          <Form.Item name="currentSnapshot" label="Snapshot">
            <Select style={{ width: 120 }} options={options.snapshots} />
          </Form.Item>
          <Form.Item name="fiscalYear" label={'財務年度'}>
            <Select style={{ width: 120 }} options={options.fiscalYear} />
          </Form.Item>
          {compare !== 'period' && (
            <Form.Item label={'評価期間'}>
              <Space>
                <Form.Item name="evaluationPeriod" noStyle>
                  <Select style={{ width: 120 }} options={options.evaluationPeriod} />
                </Form.Item>
                <Form.Item name="aggregationPeriod" noStyle>
                  <DatePicker.RangePicker style={{ width: 240 }} />
                </Form.Item>
              </Space>
            </Form.Item>
          )}
          <Form.Item name="dimension" label={'粒度'}>
            <Select style={{ width: 120 }} options={options.dimension} />
          </Form.Item>
          <CompareComponent />
          <DrillFieldsComponent />
          <DrillDownsComponent />
        </Form>
      </Modal>
    </>
  );
};

export default ForecastHeader;
